import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, X, Check } from "lucide-react";
import api from "@/lib/api";

/**
 * NotificationBell — header widget for authenticated owners.
 *
 * Behaviour:
 *   * Polls `/api/owner/notifications` every 60s while the user is on the site.
 *     Not real-time (no websockets) but sufficient for subscription reminders
 *     which change at most a few times per day per user.
 *   * Bell shows an unread count badge (capped at 9+ for visual tidiness).
 *   * Clicking the bell opens a lightweight popover with the last ~10 alerts.
 *     Clicking a row navigates to its CTA and marks it read.
 *   * "Mark all read" + per-item dismiss are supported.
 *
 * The component gracefully no-ops for admins and for unauth users — the
 * backend returns 401 which we swallow, so it's safe to render everywhere
 * in the header.
 */

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

export default function NotificationBell({ enabled = true }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get("/owner/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (err) {
      // 401 (visitor / non-owner) is expected — the bell just stays empty.
      // Anything else is a genuine failure worth logging (but never toasted:
      // the bell must not scream at users on every network blip).
      if (process.env.NODE_ENV !== "production" && err?.response?.status !== 401) {
        console.warn("Owner notifications fetch failed:", err);
      }
    }
  }, [enabled]);

  // Poll every 60s + initial load
  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/owner/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch (err) {
      // Local state already reflects the intent, so we don't rollback — just log.
      // A subsequent poll cycle will resync from the server.
      if (process.env.NODE_ENV !== "production") console.warn("mark-all-read failed:", err);
    }
  };

  const dismiss = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.delete(`/owner/notifications/${id}`);
      setItems((prev) => prev.filter((n) => n.id !== id));
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* ignore */ }
  };

  const openItem = async (n) => {
    if (!n.read_at) {
      try { await api.post(`/owner/notifications/${n.id}/read`); } catch { /* ignore */ }
      setUnread((u) => Math.max(0, u - 1));
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
    }
    setOpen(false);
  };

  if (!enabled) return null;

  return (
    <div ref={rootRef} className="relative" data-testid="notification-bell-root">
      <button
        type="button"
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell-button"
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-full text-[#1F2937] hover:bg-[#F5EFE6] transition-colors"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#B42318] text-white text-[10px] font-semibold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 mt-2 w-[340px] max-w-[90vw] rounded-2xl bg-white border border-[#E7DDCF] shadow-[0_20px_50px_rgba(17,24,39,0.12)] z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E7DDCF]">
            <div className="font-heading text-base text-[#111827]">Notifications</div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                data-testid="notification-mark-all-read"
                className="text-xs text-[#1F4E3D] hover:underline flex items-center gap-1"
              >
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto divide-y divide-[#F5EFE6]">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-[#4B5563]">
                You&apos;re all caught up.
              </div>
            ) : items.map((n) => {
              const isUnread = !n.read_at;
              const inner = (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.kind}`}
                  className={`group relative px-4 py-3 hover:bg-[#FBF7F0] transition-colors cursor-pointer ${isUnread ? "bg-[#F5EFE6]" : "bg-white"}`}
                >
                  <div className="flex items-start gap-2">
                    {isUnread && (
                      <span aria-hidden className="mt-1.5 h-2 w-2 rounded-full bg-[#C9A86A] shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-[#111827] leading-snug">{n.title}</div>
                      <div className="text-xs text-[#4B5563] mt-1 leading-snug line-clamp-2">{n.body}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#7A5A22] mt-1.5">{timeAgo(n.created_at)}</div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => dismiss(n.id, e)}
                      aria-label="Dismiss"
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 inline-flex items-center justify-center rounded-full text-[#7A5A22] hover:bg-[#F5EFE6]"
                      data-testid={`notification-dismiss-${n.id}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
              );

              if (n.cta_url) {
                return (
                  <Link key={n.id} to={n.cta_url} onClick={() => openItem(n)}>
                    {inner}
                  </Link>
                );
              }
              return <div key={n.id} onClick={() => openItem(n)}>{inner}</div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}
